import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface ProcessStep {
  step: number;
  title: string;
  description: string;
}

interface CaseStudy {
  title: string;
  client: string;
  industry: string;
  challenge: string;
  solution: string;
  results: {
    metric: string;
    value: string;
  }[];
  image: string;
}

interface Testimonial {
  name: string;
  role: string;
  company: string;
  content: string;
  avatar: string;
}

@Component({
  selector: 'app-bulk-email',
  imports: [CommonModule, RouterLink],
  templateUrl: './bulk-email.html',
  styleUrl: './bulk-email.css',
  standalone: true
})
export class BulkEmail {
  features: Feature[] = [
    {
      icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
      title: 'Drag & Drop Email Builder',
      description: 'Create stunning responsive emails without coding. Choose from 200+ professionally designed templates or build your own with our intuitive visual editor.'
    },
    {
      icon: 'M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z',
      title: 'Advanced Segmentation',
      description: 'Target the right audience with powerful segmentation tools. Create dynamic segments based on behavior, demographics, engagement history, and custom fields.'
    },
    {
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
      title: 'Real-Time Analytics',
      description: 'Track opens, clicks, conversions, and revenue attribution. Visual dashboards show campaign performance at a glance with detailed drill-down reports.'
    },
    {
      icon: 'M13 10V3L4 14h7v7l9-11h-7z',
      title: 'Marketing Automation',
      description: 'Set up automated workflows triggered by user actions. Welcome series, abandoned cart recovery, re-engagement campaigns, and more run on autopilot.'
    },
    {
      icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      title: 'Superior Deliverability',
      description: 'Industry-leading inbox placement rates with dedicated IP addresses, SPF/DKIM/DMARC authentication, and proactive reputation management.'
    },
    {
      icon: 'M4 5a1 1 0 011-1h4a1 1 0 010 2H6v10h4a1 1 0 110 2H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 110 2h-3v10h3a1 1 0 110 2h-4a1 1 0 01-1-1V5z',
      title: 'A/B Testing',
      description: 'Optimize campaign performance with multivariate testing. Test subject lines, content, send times, and sender names to maximize engagement and ROI.'
    }
  ];

  process: ProcessStep[] = [
    {
      step: 1,
      title: 'Design Your Email',
      description: 'Choose a template or start from scratch. Customize with your brand colors, images, and content using our drag-and-drop builder. Mobile-responsive by default.'
    },
    {
      step: 2,
      title: 'Build Your Audience',
      description: 'Import contacts, create segments, and define targeting rules. Clean and validate email lists automatically to maintain high deliverability.'
    },
    {
      step: 3,
      title: 'Test & Optimize',
      description: 'Preview across devices, send test emails, and run spam checks. Set up A/B tests to determine the most effective messaging and timing.'
    },
    {
      step: 4,
      title: 'Send & Analyze',
      description: 'Schedule delivery or send immediately. Monitor real-time performance metrics and gain insights to improve future campaigns.'
    }
  ];

  caseStudy: CaseStudy = {
    title: 'SaaS Product Launch & Nurture Campaign',
    client: 'CloudTech Solutions',
    industry: 'Software as a Service (SaaS)',
    challenge: 'CloudTech needed to launch a new product to 100,000 leads while nurturing them through a complex B2B sales cycle. Their existing email provider had poor deliverability and lacked sophisticated automation capabilities.',
    solution: 'Deployed HoneyKode\'s Bulk Email platform with multi-stage automated nurture sequences, behavioral triggers, and personalized content paths. Implemented dedicated IP warmup and advanced segmentation based on product interest and engagement levels.',
    results: [
      { metric: 'Email Deliverability', value: '98.4%' },
      { metric: 'Average Open Rate', value: '42%' },
      { metric: 'Click-Through Rate', value: '18.5%' },
      { metric: 'Lead-to-Customer', value: '12.3%' },
      { metric: 'Campaign ROI', value: '847%' },
      { metric: 'Time to Conversion', value: '38% faster' }
    ],
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&h=500&fit=crop'
  };

  testimonial: Testimonial = {
    name: 'Jennifer Park',
    role: 'Director of Marketing',
    company: 'CloudTech Solutions',
    content: 'HoneyKode\'s email platform transformed our marketing operations. The automation capabilities allowed us to create sophisticated nurture campaigns that would have been impossible before. Our open rates doubled, and more importantly, we saw a 12% conversion rate from email leads. The analytics give us deep insights into what\'s working, and the deliverability is the best we\'ve ever experienced. This platform is an absolute must-have for any serious B2B marketer.',
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop'
  };
}
